import Box from '@mui/material/Box';
import MuiButton from '@mui/material/Button';

import { useAppDispatch, useAppSelector } from 'hooks';
import type React from 'react';

import {
  selectLegibleTextColor,
  selectLegibleElementColor,
  selectLegibleButtonOutlineColor,
  selectIsInteractiveEmail,
} from 'reducers/config';
import Typography from '@mui/material/Typography';
import {
  selectHasBeenInteractedWith,
  selectInteractiveEmailDeflectionFeedback,
  selectWorkflowsConfig,
  setInteractiveEmailDeflectionFeedback,
} from 'reducers/workflows';

export const InteractiveEmailFeedbackHeader = () => {
  const dispatch = useAppDispatch();
  const isInteractiveEmail = useAppSelector(selectIsInteractiveEmail);
  const { interactive_email_static_text: staticText } =
    useAppSelector(selectWorkflowsConfig) ?? {};
  const interactiveEmailDeflectionFeedback = useAppSelector(
    selectInteractiveEmailDeflectionFeedback,
  );
  const hasBeenInteractedWith = useAppSelector(selectHasBeenInteractedWith);
  const isOpen = isInteractiveEmail && hasBeenInteractedWith;

  if (!isOpen) {
    return null;
  }

  return (
    <Header
      buttons={
        <>
          <FeedbackButton
            isSelected={interactiveEmailDeflectionFeedback === 'deflected'}
            onClick={() =>
              dispatch(setInteractiveEmailDeflectionFeedback('deflected'))
            }
          >
            {staticText?.yes}
          </FeedbackButton>
          <FeedbackButton
            isSelected={interactiveEmailDeflectionFeedback === 'not-deflected'}
            onClick={() =>
              dispatch(setInteractiveEmailDeflectionFeedback('not-deflected'))
            }
          >
            {staticText?.no}
          </FeedbackButton>
        </>
      }
      text={
        <>
          <Typography variant='font14'>
            {staticText?.is_issue_resolved}
          </Typography>
          <Typography variant='font14'>
            {staticText?.feedback_description}
          </Typography>
        </>
      }
    />
  );
};

interface HeaderProps {
  buttons?: React.ReactNode;
  submitButton?: React.ReactNode;
  text: React.ReactNode;
}

const Header = ({ buttons, submitButton, text }: HeaderProps) => {
  return (
    <Box
      alignItems='center'
      boxShadow={theme => theme.shadows[3]}
      boxSizing='border-box'
      display='flex'
      flexDirection='column'
      gap={3}
      position='sticky'
      px={4}
      py={3}
      sx={{ borderBottomLeftRadius: '12px', borderBottomRightRadius: '12px' }}
      width='100%'
    >
      {text && (
        <Box
          alignItems='center'
          display='flex'
          flexDirection='column'
          textAlign='center'
        >
          {text}
        </Box>
      )}
      {buttons && (
        <Box display='flex' gap={1}>
          {buttons}
        </Box>
      )}
      {submitButton}
    </Box>
  );
};

const FeedbackButton = ({
  children,
  isSelected = false,
  onClick,
}: {
  children?: React.ReactNode;
  isSelected?: boolean;
  onClick: () => void;
}) => {
  const legibleElementColor = useAppSelector(selectLegibleElementColor);
  const legibleTextColor = useAppSelector(selectLegibleTextColor);
  const legibleButtonOutlineColor = useAppSelector(
    selectLegibleButtonOutlineColor,
  );

  return (
    <MuiButton
      onClick={onClick}
      sx={[
        {
          '&&.Mui-focusVisible': {
            outline: `${legibleButtonOutlineColor} solid 2px`,
          },
          '&&:hover': {
            outline: `${legibleButtonOutlineColor} solid 2px`,
          },
          border: `1px solid ${legibleElementColor}`,
          borderRadius: '20px',
          color: legibleElementColor,
          p: '8px 48px',
        },
        isSelected && {
          backgroundColor: legibleElementColor,
          color: legibleTextColor,
        },
      ]}
      variant={isSelected ? 'contained' : 'outlined'}
    >
      <Typography variant='font14Medium'>{children}</Typography>
    </MuiButton>
  );
};
